import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag, Share2, Twitter, Linkedin } from 'lucide-react';
import { supabase } from '../../services/supabase';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  meta_description: string;
  keywords: string[];
  featured_image: string | null;
  author_name: string;
  category: string;
  published_at: string;
}

function parseMarkdown(content: string): string {
  let html = content;
  html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-heading font-bold text-slate-900 mt-8 mb-4">$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-heading font-bold text-slate-900 mt-10 mb-4">$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1 class="text-3xl font-heading font-bold text-slate-900 mt-12 mb-6">$1</h1>');
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/^\d+\. (.*$)/gim, '<li class="ml-6 mb-2 list-decimal">$1</li>');
  html = html.replace(/^- (.*$)/gim, '<li class="ml-6 mb-2 list-disc">$1</li>');
  html = html.replace(/\n\n/g, '</p><p class="mb-4 text-slate-600 leading-relaxed">');
  html = '<p class="mb-4 text-slate-600 leading-relaxed">' + html + '</p>';
  html = html.replace(/<p class="mb-4 text-slate-600 leading-relaxed"><h/g, '<h');
  html = html.replace(/<\/h([1-3])><\/p>/g, '</h$1>');
  html = html.replace(/<p class="mb-4 text-slate-600 leading-relaxed"><li/g, '<ul class="my-4"><li');
  html = html.replace(/<\/li><\/p>/g, '</li></ul>');

  return html;
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    async function fetchPost() {
      if (!slug) return;

      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();

      if (error || !data) {
        navigate('/blog');
        return;
      }

      setPost(data);

      document.title = `${data.title} | VZMO Blog`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', data.meta_description || data.excerpt);
      }

      const { data: related } = await supabase
        .from('blog_posts')
        .select('id, slug, title, excerpt, category, published_at')
        .eq('is_published', true)
        .eq('category', data.category)
        .neq('id', data.id)
        .limit(3);

      if (related) {
        setRelatedPosts(related);
      }

      setLoading(false);
    }

    fetchPost();
  }, [slug, navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = content.split(' ').length;
    return Math.max(3, Math.ceil(words / wordsPerMinute));
  };

  const shareOnTwitter = () => {
    if (!post) return;
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(post.title);
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    if (!post) return;
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
  };

  if (loading) {
    return (
      <div className="bg-white pt-20 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-24">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="bg-white pt-20">
      <article>
        <header className="py-16 sm:py-24 bg-gradient-to-b from-[#FDF8F4] to-white">
          <div className="max-w-4xl mx-auto px-4">
            <Link
              to="/blog"
              className="inline-flex items-center text-slate-600 hover:text-coral-600 transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to blog
            </Link>

            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-coral-100 text-coral-700 text-xs font-medium rounded-full capitalize">
                <Tag className="h-3 w-3" />
                {post.category}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6 leading-tight">
              {post.title}
            </h1>

            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              {post.excerpt}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {formatDate(post.published_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {estimateReadTime(post.content)} min read
              </span>
              <span>By {post.author_name}</span>
            </div>
          </div>
        </header>

        {post.featured_image && (
          <div className="max-w-5xl mx-auto px-4 -mt-8">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-64 sm:h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        )}

        <div className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4">
            <div
              className="prose prose-slate prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(post.content) }}
            />

            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex flex-wrap gap-2">
                  {post.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 mr-2">Share:</span>
                  <button
                    onClick={shareOnTwitter}
                    className="p-2 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="h-4 w-4" />
                  </button>
                  <button
                    onClick={shareOnLinkedIn}
                    className="p-2 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="h-4 w-4" />
                  </button>
                  <button
                    onClick={copyLink}
                    className="p-2 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                    aria-label="Copy link"
                  >
                    <Share2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="py-16 bg-coral-50">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-4">
              Ready to try AI coaching?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Get personalized guidance for your career, relationships, and goals.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center px-8 py-4 bg-slate-900 text-white rounded-full font-semibold text-lg hover:bg-slate-800 transition-all shadow-lg"
            >
              Start free trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </section>
      </article>

      {relatedPosts.length > 0 && (
        <section className="py-16 bg-white border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-8">
              Related articles
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map(relatedPost => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group"
                >
                  <div className="bg-[#FDFBF7] rounded-xl p-6 border border-slate-100 hover:border-coral-200 transition-all hover:shadow-md h-full">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-coral-100 text-coral-700 text-xs font-medium rounded-full capitalize mb-3">
                      {relatedPost.category}
                    </span>
                    <h3 className="font-heading font-bold text-slate-900 mb-2 group-hover:text-coral-600 transition-colors">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
